/*
  Huid zonder filter: the three movements that need script.

  Two of the five live entirely in hzf-motion.css (the card hover and the
  CTA gradient). These three cannot:

  - section entrance needs to know when a section is in view;
  - the cart count needs to notice that Dawn replaced the header bubble;
  - <details> has no height to transition between.

  No library, no build step, no dependency on Dawn's own scripts. Every
  branch below degrades to "the thing still works, without the movement":
  that is why the hidden starting state for the entrance is added by this
  file rather than shipped in the CSS.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: null };

  var DURATION = 240;
  var PULSE = 280;
  var EASE = 'cubic-bezier(0.2, 0, 0, 1)';

  /* ------------------------------------------------------------------
     1. Section entrance.

     The class goes on <html> first, so the CSS starting state only ever
     applies once this script is running. Sections already on screen when
     the page loads are revealed immediately rather than animated in:
     animating what the visitor is already looking at is a delay, not a
     flourish.
  ------------------------------------------------------------------ */
  function setUpReveal() {
    if (!('IntersectionObserver' in window)) return;

    var sections = document.querySelectorAll('.shopify-section');
    if (!sections.length) return;

    root.classList.add('hzf-motion');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('hzf-reveal--in');
          // Once only: the section never fades back out on the way up.
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    Array.prototype.forEach.call(sections, function (section) {
      section.classList.add('hzf-reveal');
      // Anything already in the viewport is shown without animating.
      var box = section.getBoundingClientRect();
      if (box.top < window.innerHeight) {
        section.classList.add('hzf-reveal--in');
        return;
      }
      observer.observe(section);
    });
  }

  /* ------------------------------------------------------------------
     2. Cart count.

     Dawn re-renders #cart-icon-bubble through section rendering after an
     add, so there is no event to listen for and the element we want is
     replaced rather than updated. A MutationObserver on the container
     survives that, and the count is read back out of the fresh markup.

     The number counts up rather than jumping, capped at a handful of
     steps so a jump from 1 to 40 does not take longer than a jump from
     1 to 2. Under reduced motion the number is simply correct, at once.
  ------------------------------------------------------------------ */
  function currentCount(container) {
    var bubble = container.querySelector('.cart-count-bubble [aria-hidden="true"]');
    if (!bubble) return 0;
    var value = parseInt(bubble.textContent.trim(), 10);
    return isNaN(value) ? 0 : value;
  }

  function countUp(node, from, to, done) {
    var steps = Math.min(Math.abs(to - from), 8);
    if (steps < 1 || reduce.matches) {
      node.textContent = String(to);
      done();
      return;
    }
    var step = 0;
    var stepMs = Math.round(PULSE / (steps + 1));
    var timer = setInterval(function () {
      step += 1;
      var value = Math.round(from + ((to - from) * step) / steps);
      node.textContent = String(value);
      if (step >= steps) {
        node.textContent = String(to);
        clearInterval(timer);
        done();
      }
    }, stepMs);
  }

  function setUpCartCount() {
    var container = document.getElementById('cart-icon-bubble');
    if (!container || !('MutationObserver' in window)) return;

    var previous = currentCount(container);
    // Counting up writes to the very node the observer is watching, so
    // without this the animation retriggers itself on every step: the
    // pulse restarts four times and never finishes cleanly.
    var updating = false;

    new MutationObserver(function () {
      if (updating) return;
      var next = currentCount(container);
      if (next === previous) return;

      var bubble = container.querySelector('.cart-count-bubble');
      var node = container.querySelector('.cart-count-bubble [aria-hidden="true"]');

      if (bubble && node && next > previous) {
        updating = true;
        countUp(node, previous, next, function () {
          updating = false;
        });
        if (!reduce.matches) {
          bubble.classList.remove('hzf-cart-pulse');
          // Reading offsetWidth restarts the animation on a class that is
          // being re-added within the same frame.
          void bubble.offsetWidth;
          bubble.classList.add('hzf-cart-pulse');
          bubble.addEventListener(
            'animationend',
            function () {
              bubble.classList.remove('hzf-cart-pulse');
            },
            { once: true }
          );
        }
      }
      previous = next;
    }).observe(container, { childList: true, subtree: true, characterData: true });
  }

  /* ------------------------------------------------------------------
     3. Accordions.

     <details> toggles instantly and the page jumps by the height of the
     answer. Opening is easy: let the browser open it, then animate from
     zero to the measured height. Closing needs the panel to stay in the
     document until the animation ends, so the default is prevented and
     the open attribute is removed at the end.

     If Element.animate is missing the listener never attaches and the
     accordion behaves exactly as the browser intends.
  ------------------------------------------------------------------ */
  function animateAccordion(details, summary) {
    var content = details.querySelector('.accordion__content');
    if (!content) return;

    summary.addEventListener('click', function (event) {
      if (reduce.matches) return;
      event.preventDefault();

      if (details.dataset.hzfAnimating === 'true') return;
      details.dataset.hzfAnimating = 'true';
      details.classList.add('hzf-accordion--animating');

      var opening = !details.open;
      if (opening) details.open = true;

      var height = content.getBoundingClientRect().height;
      var frames = opening ? [{ height: '0px' }, { height: height + 'px' }] : [{ height: height + 'px' }, { height: '0px' }];

      var animation = content.animate(frames, { duration: DURATION, easing: EASE });

      animation.onfinish = function () {
        if (!opening) details.open = false;
        details.classList.remove('hzf-accordion--animating');
        details.dataset.hzfAnimating = 'false';
      };
    });
  }

  function setUpAccordions() {
    if (!Element.prototype.animate) return;
    var items = document.querySelectorAll('.accordion details');
    Array.prototype.forEach.call(items, function (details) {
      var summary = details.querySelector('summary');
      if (summary) animateAccordion(details, summary);
    });
  }

  function init() {
    if (!reduce.matches) setUpReveal();
    setUpCartCount();
    setUpAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
